import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft,
  Users,
  MapPin,
  Star,
  AlertTriangle,
  Check,
  X,
  BarChart3,
  Clock,
  Shield,
  Droplets,
  MessageSquare,
  Map as MapIcon,
  Activity,
  Trash2,
  EyeOff,
  Eye,
  Building2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small bar-chart component (dependency-free)
const BarChart = ({ rows, valueKey = "count", labelKey = "label", color = "#0ea5e9", max }) => {
  if (!rows || rows.length === 0) {
    return <p className="text-slate-400 text-sm py-6 text-center">No data yet</p>;
  }
  const highest = max || Math.max(...rows.map((r) => r[valueKey] || 0), 1);
  return (
    <div className="space-y-2" data-testid="bar-chart">
      {rows.map((r, i) => {
        const v = r[valueKey] || 0;
        const w = Math.max(2, Math.round((v / highest) * 100));
        return (
          <div key={`${r[labelKey]}-${i}`} className="flex items-center gap-3 text-sm">
            <div className="w-32 shrink-0 truncate text-slate-700 font-medium">{r[labelKey]}</div>
            <div className="flex-1 bg-slate-100 rounded-full overflow-hidden h-4">
              <div
                className="h-4 rounded-full transition-all"
                style={{ width: `${w}%`, background: color }}
              />
            </div>
            <div className="w-14 text-right tabular-nums text-slate-600">{v.toLocaleString()}</div>
          </div>
        );
      })}
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingRestrooms, setPendingRestrooms] = useState([]);
  const [reports, setReports] = useState([]);
  const [flaggedReviews, setFlaggedReviews] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const [utilisation, setUtilisation] = useState({ daily: [], hourly: [] });
  const [errorFeed, setErrorFeed] = useState({ recent: [], top: [], by_kind: [] });
  const [overview, setOverview] = useState(null);
  const [modules, setModules] = useState({ modules: [], events: [] });
  const [geo, setGeo] = useState({ rows: [], top_user_state: null });
  const [loading, setLoading] = useState(true);
  const [reviewFilter, setReviewFilter] = useState("flagged");

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user?.role === "admin") fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFilter]);

  const authCfg = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const fetchAll = async () => {
    try {
      const [statsRes, pendingRes, reportsRes, overviewRes, modulesRes, geoRes, utilRes, errRes, recentRevRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, authCfg()),
        axios.get(`${API}/admin/pending-restrooms`, authCfg()),
        axios.get(`${API}/admin/reports`, authCfg()),
        axios.get(`${API}/admin/analytics/overview`, authCfg()),
        axios.get(`${API}/admin/analytics/module-usage?days=30`, authCfg()),
        axios.get(`${API}/admin/analytics/geography`, authCfg()),
        axios.get(`${API}/admin/analytics/utilisation?days=7`, authCfg()),
        axios.get(`${API}/admin/analytics/errors?days=7&limit=100`, authCfg()),
        axios.get(`${API}/admin/reviews/recent?limit=50`, authCfg()),
      ]);
      setStats(statsRes.data);
      setPendingRestrooms(pendingRes.data);
      setReports(reportsRes.data);
      setOverview(overviewRes.data);
      setModules(modulesRes.data);
      setGeo(geoRes.data);
      setUtilisation(utilRes.data);
      setErrorFeed(errRes.data);
      setRecentReviews(recentRevRes.data.reviews || []);
      await fetchReviews();
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/admin/reviews?status=${reviewFilter}&limit=200`, authCfg());
      setFlaggedReviews(res.data.reviews || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleApproveRestroom = async (restroomId) => {
    try {
      await axios.put(`${API}/admin/restrooms/${restroomId}/approve`, {}, authCfg());
      setPendingRestrooms((prev) => prev.filter((r) => r.restroom_id !== restroomId));
      setStats((prev) => prev && ({
        ...prev,
        pending_restrooms: Math.max(0, (prev.pending_restrooms || 0) - 1),
        total_restrooms: (prev.total_restrooms || 0) + 1,
      }));
      toast.success("Restroom approved");
    } catch (error) {
      toast.error("Failed to approve restroom");
    }
  };

  const handleRejectRestroom = async (restroomId) => {
    try {
      await axios.put(`${API}/admin/restrooms/${restroomId}/reject`, {}, authCfg());
      setPendingRestrooms((prev) => prev.filter((r) => r.restroom_id !== restroomId));
      setStats((prev) => prev && ({
        ...prev,
        pending_restrooms: Math.max(0, (prev.pending_restrooms || 0) - 1),
      }));
      toast.success("Restroom rejected");
    } catch (error) {
      toast.error("Failed to reject restroom");
    }
  };

  const handleResolveReport = async (reportId, action) => {
    try {
      await axios.put(`${API}/admin/reports/${reportId}/resolve?action=${action}`, {}, authCfg());
      setReports((prev) => prev.filter((r) => r.report_id !== reportId));
      setStats((prev) => prev && ({
        ...prev,
        pending_reports: Math.max(0, (prev.pending_reports || 0) - 1),
      }));
      toast.success("Report resolved");
    } catch (error) {
      toast.error("Failed to resolve report");
    }
  };

  const moderateReview = async (reviewId, action) => {
    try {
      if (action === "delete") {
        await axios.delete(`${API}/admin/reviews/${reviewId}`, authCfg());
      } else {
        await axios.post(`${API}/admin/reviews/${reviewId}/${action}`, {}, authCfg());
      }
      setFlaggedReviews((prev) => prev.filter((r) => r.review_id !== reviewId));
      toast.success(`Review ${action}d`);
    } catch (e) {
      toast.error("Action failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-slate-500 hover:text-slate-700"
              data-testid="admin-back-btn"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back
            </button>
            <h1 className="text-lg font-bold ml-4 flex items-center gap-2">
              <BarChart3 size={20} className="text-sky-600" />
              Admin Dashboard
            </h1>
          </div>
          <Badge className="bg-purple-100 text-purple-700">Admin</Badge>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-6 space-y-6">
        {/* Top Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
          data-testid="admin-stats-grid"
        >
          <StatCard icon={<Users size={20} className="text-sky-600" />} bg="bg-sky-100" value={overview?.users?.total || 0} label="Total Users" testid="stat-users" />
          <StatCard icon={<MapPin size={20} className="text-green-600" />} bg="bg-green-100" value={overview?.restrooms?.approved || 0} label="Approved Locations" testid="stat-locations" />
          <StatCard icon={<Star size={20} className="text-amber-600" />} bg="bg-amber-100" value={overview?.reviews?.total || 0} label="Total Reviews" testid="stat-reviews" />
          <StatCard icon={<AlertTriangle size={20} className="text-red-600" />} bg="bg-red-100" value={(overview?.reviews?.flagged || 0) + (stats?.pending_reports || 0)} label="Needs Moderation" testid="stat-moderation" />
        </motion.div>

        {/* Second row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <MiniStat icon={<Clock size={18} className="text-slate-400" />} value={overview?.users?.new_this_week || 0} label="New users this week" />
          <MiniStat icon={<Activity size={18} className="text-slate-400" />} value={overview?.users?.active_24h || 0} label="Active in 24h" />
          <MiniStat icon={<MapIcon size={18} className="text-slate-400" />} value={overview?.restrooms?.pending || 0} label="Pending locations" />
          <MiniStat icon={<Shield size={18} className="text-slate-400" />} value={overview?.sos?.week || 0} label="SOS this week" />
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Tabs defaultValue="pending" className="w-full">
            <TabsList className="grid w-full grid-cols-4 md:grid-cols-8 mb-4 gap-1">
              <TabsTrigger value="pending" data-testid="tab-pending" className="data-[state=active]:bg-sky-100 data-[state=active]:text-sky-700">
                Pending ({pendingRestrooms.length})
              </TabsTrigger>
              <TabsTrigger value="recent" data-testid="tab-recent" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
                Recent ({recentReviews.length})
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700">
                Moderation ({flaggedReviews.length})
              </TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports" className="data-[state=active]:bg-red-100 data-[state=active]:text-red-700">
                Reports ({reports.length})
              </TabsTrigger>
              <TabsTrigger value="utilisation" data-testid="tab-utilisation" className="data-[state=active]:bg-teal-100 data-[state=active]:text-teal-700">
                Utilisation
              </TabsTrigger>
              <TabsTrigger value="errors" data-testid="tab-errors" className="data-[state=active]:bg-rose-100 data-[state=active]:text-rose-700">
                Errors ({errorFeed.recent?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics" className="data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700">
                Modules
              </TabsTrigger>
              <TabsTrigger value="geography" data-testid="tab-geography" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700">
                Geography
              </TabsTrigger>
            </TabsList>

            {/* PENDING LOCATIONS */}
            <TabsContent value="pending">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin size={18} className="text-sky-600" />
                    Pending Location Submissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingRestrooms.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No pending submissions 🎉</p>
                  ) : (
                    <ScrollArea className="max-h-[520px]">
                      <div className="space-y-3">
                        {pendingRestrooms.map((r) => (
                          <div key={r.restroom_id} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-slate-900 truncate">{r.name}</h3>
                                <p className="text-sm text-slate-500 mt-1 truncate">{r.address}</p>
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  <Badge variant="secondary" className="text-xs">
                                    {r.type === "paid" ? `Paid ₹${r.price ?? ""}` : "Free"}
                                  </Badge>
                                  {r.category && <Badge variant="outline" className="text-xs">{r.category}</Badge>}
                                  {r.is_24_hours && <Badge variant="secondary" className="text-xs">24h</Badge>}
                                  {(r.attributes || []).map((a) => (
                                    <Badge key={a} variant="outline" className="text-xs">{a}</Badge>
                                  ))}
                                </div>
                                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                  <span>Submitted: {new Date(r.created_at).toLocaleString()}</span>
                                  <a
                                    className="text-sky-600 hover:underline"
                                    href={`https://www.openstreetmap.org/?mlat=${r.latitude}&mlon=${r.longitude}#map=17/${r.latitude}/${r.longitude}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    View on map ↗
                                  </a>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApproveRestroom(r.restroom_id)}
                                  className="bg-green-600 hover:bg-green-700"
                                  data-testid={`approve-${r.restroom_id}`}
                                >
                                  <Check size={16} className="mr-1" /> Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectRestroom(r.restroom_id)}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  data-testid={`reject-${r.restroom_id}`}
                                >
                                  <X size={16} className="mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* RECENT REVIEWS (newest-first feed) */}
            <TabsContent value="recent">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare size={18} className="text-indigo-600" />
                    Recent reviews (newest first)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentReviews.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No reviews yet.</p>
                  ) : (
                    <ScrollArea className="max-h-[520px]">
                      <div className="space-y-3">
                        {recentReviews.map((r) => (
                          <div key={r.review_id} className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-testid={`recent-review-${r.review_id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={r.moderation_passed ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                                {r.moderation_passed ? "passed" : "flagged"}
                              </Badge>
                              <span className="text-sm font-semibold text-slate-800 truncate">
                                {r.restroom?.name || r.restroom_id}
                              </span>
                              {r.restroom?.city && <span className="text-xs text-slate-400">· {r.restroom.city}, {r.restroom.state}</span>}
                              <span className="ml-auto text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                            </div>
                            {r.comment && (
                              <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words">{r.comment}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                              <span className="inline-flex items-center gap-1"><Shield size={12} /> {r.safety_rating}</span>
                              <span className="inline-flex items-center gap-1"><Droplets size={12} /> {r.cleanliness_rating}</span>
                              {r.moderation_source && (
                                <span className="text-slate-500">· via {r.moderation_source}{r.moderation_reason ? ` — ${r.moderation_reason}` : ""}</span>
                              )}
                              <span>· by {r.is_anonymous ? "anonymous" : (r.user_name || "user")}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* UTILISATION */}
            <TabsContent value="utilisation">
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity size={18} className="text-teal-600" />
                      Daily activity (last {utilisation.daily?.length || 7} days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChart
                      rows={(utilisation.daily || []).map((d) => ({ label: d.day, count: d.events }))}
                      color="#0d9488"
                    />
                    <p className="text-xs text-slate-500 mt-3">
                      Peak day:{" "}
                      {utilisation.daily && utilisation.daily.length ? (() => {
                        const p = [...utilisation.daily].sort((a, b) => (b.events || 0) - (a.events || 0))[0];
                        return `${p.day} · ${p.events} events · ${p.unique_users} users`;
                      })() : "—"}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock size={18} className="text-teal-600" />
                      Hour-of-day usage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChart
                      rows={(utilisation.hourly || []).map((h) => ({ label: `${String(h.hour).padStart(2, "0")}:00`, count: h.count }))}
                      color="#14b8a6"
                    />
                  </CardContent>
                </Card>
              </div>

              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-lg">Uniques snapshot</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MiniStat icon={<Users size={16} className="text-teal-600" />} label="Unique users · 7d" value={
                      (utilisation.daily || []).reduce((acc, d) => acc + (d.unique_users || 0), 0)
                    } />
                    <MiniStat icon={<Activity size={16} className="text-teal-600" />} label="Total events · 7d" value={
                      (utilisation.daily || []).reduce((acc, d) => acc + (d.events || 0), 0)
                    } />
                    <MiniStat icon={<MapIcon size={16} className="text-teal-600" />} label="Unique IPs · 7d" value={
                      (utilisation.daily || []).reduce((acc, d) => acc + (d.unique_ips || 0), 0)
                    } />
                    <MiniStat icon={<BarChart3 size={16} className="text-teal-600" />} label="Avg events/day" value={
                      (utilisation.daily || []).length
                        ? Math.round((utilisation.daily || []).reduce((acc, d) => acc + (d.events || 0), 0) / utilisation.daily.length)
                        : 0
                    } />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ERRORS + DISCONNECTS */}
            <TabsContent value="errors">
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle size={18} className="text-rose-600" />
                      Errors by kind (7d)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChart
                      rows={(errorFeed.by_kind || []).map((k) => ({ label: k.kind, count: k.count }))}
                      color="#e11d48"
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle size={18} className="text-rose-600" />
                      Most common messages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="max-h-[380px]">
                      <div className="space-y-2">
                        {(errorFeed.top || []).map((t, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm bg-rose-50 border border-rose-100 rounded-lg p-2">
                            <Badge variant="outline" className="text-rose-700 border-rose-200 mt-0.5">{t.kind}</Badge>
                            <span className="flex-1 text-slate-700 break-words">{t.message || <em className="text-slate-400">no message</em>}</span>
                            <span className="text-xs text-slate-500 shrink-0 tabular-nums">×{t.count}</span>
                          </div>
                        ))}
                        {(errorFeed.top || []).length === 0 && (
                          <p className="text-sm text-slate-400 py-4 text-center">No errors reported — nice.</p>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-lg">Recent client events (errors + disconnects)</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="max-h-[420px]">
                    <table className="w-full text-sm" data-testid="errors-table">
                      <thead className="text-left text-slate-500 border-b border-slate-100">
                        <tr>
                          <th className="py-2 pr-3">When</th>
                          <th className="py-2 pr-3">Kind</th>
                          <th className="py-2 pr-3">Path</th>
                          <th className="py-2 pr-3">Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(errorFeed.recent || []).map((e) => (
                          <tr key={e.id} className="border-b border-slate-50">
                            <td className="py-2 pr-3 text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                            <td className="py-2 pr-3"><Badge variant="outline" className="text-rose-600 border-rose-200">{e.kind}</Badge></td>
                            <td className="py-2 pr-3 text-slate-600 truncate max-w-[160px]">{e.path || "—"}</td>
                            <td className="py-2 pr-3 text-slate-700 break-words">{e.message || <em className="text-slate-400">—</em>}</td>
                          </tr>
                        ))}
                        {(errorFeed.recent || []).length === 0 && (
                          <tr><td colSpan={4} className="text-center py-6 text-slate-400">No error events reported.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="reviews">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare size={18} className="text-amber-600" />
                      Review Moderation Queue
                    </CardTitle>
                    <div className="flex gap-1 text-xs">
                      {["flagged", "passed", "all"].map((f) => (
                        <button
                          key={f}
                          onClick={() => setReviewFilter(f)}
                          data-testid={`review-filter-${f}`}
                          className={`px-3 py-1 rounded-full border transition ${
                            reviewFilter === f
                              ? "bg-amber-500 text-white border-amber-500"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {flaggedReviews.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">
                      {reviewFilter === "flagged" ? "No flagged reviews. Great job!" : "No reviews here."}
                    </p>
                  ) : (
                    <ScrollArea className="max-h-[520px]">
                      <div className="space-y-3">
                        {flaggedReviews.map((r) => (
                          <div key={r.review_id} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge className={r.moderation_passed ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                                    {r.moderation_passed ? "passed" : "flagged"}
                                  </Badge>
                                  <span className="text-sm font-semibold text-slate-800 truncate">
                                    {r.restroom?.name || r.restroom_id}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    · by {r.is_anonymous ? "anonymous" : (r.user_name || "user")}
                                  </span>
                                </div>
                                {r.comment && (
                                  <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words">
                                    {r.comment}
                                  </p>
                                )}
                                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                  <span className="inline-flex items-center gap-1">
                                    <Shield size={12} /> {r.safety_rating}
                                  </span>
                                  <span className="inline-flex items-center gap-1">
                                    <Droplets size={12} /> {r.cleanliness_rating}
                                  </span>
                                  <span>· {new Date(r.created_at).toLocaleString()}</span>
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 shrink-0">
                                {!r.moderation_passed ? (
                                  <Button
                                    size="sm"
                                    onClick={() => moderateReview(r.review_id, "approve")}
                                    className="bg-green-600 hover:bg-green-700"
                                    data-testid={`review-approve-${r.review_id}`}
                                  >
                                    <Eye size={14} className="mr-1" /> Approve
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => moderateReview(r.review_id, "hide")}
                                    className="text-amber-700 border-amber-200 hover:bg-amber-50"
                                    data-testid={`review-hide-${r.review_id}`}
                                  >
                                    <EyeOff size={14} className="mr-1" /> Hide
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => moderateReview(r.review_id, "delete")}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  data-testid={`review-delete-${r.review_id}`}
                                >
                                  <Trash2 size={14} className="mr-1" /> Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* USER REPORTS */}
            <TabsContent value="reports">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle size={18} className="text-red-600" />
                    User Reports
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reports.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No pending reports</p>
                  ) : (
                    <ScrollArea className="max-h-[520px]">
                      <div className="space-y-3">
                        {reports.map((report) => (
                          <div key={report.report_id} className="p-4 bg-slate-50 rounded-xl">
                            <div className="flex justify-between items-start gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge className={report.content_type === "review" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}>
                                    {report.content_type}
                                  </Badge>
                                  <span className="text-xs text-slate-500">ID: {report.content_id}</span>
                                </div>
                                <p className="text-sm text-slate-700 mt-2">
                                  <strong>Reason:</strong> {report.reason}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Reported: {new Date(report.created_at).toLocaleString()}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => handleResolveReport(report.report_id, "remove")} className="text-red-600 border-red-200">
                                  Remove
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleResolveReport(report.report_id, "dismiss")}>
                                  Dismiss
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ANALYTICS */}
            <TabsContent value="analytics">
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Activity size={18} className="text-emerald-600" />
                      Module Usage (last 30 days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChart
                      rows={(modules.modules || []).slice(0, 12).map((m) => ({ label: m.module || "unknown", count: m.count }))}
                      color="#10b981"
                    />
                    <p className="text-xs text-slate-400 mt-3">
                      Unique users per module:
                      {" "}
                      {(modules.modules || [])
                        .slice(0, 5)
                        .map((m) => `${m.module}=${m.unique_users}`)
                        .join(" · ")}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 size={18} className="text-sky-600" />
                      Top Events
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <BarChart
                      rows={(modules.events || []).slice(0, 12).map((m) => ({ label: m.event, count: m.count }))}
                      color="#0ea5e9"
                    />
                  </CardContent>
                </Card>
              </div>

              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-lg">Growth Snapshot</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MiniStat label="Users (all time)" value={overview?.users?.total || 0} icon={<Users size={16} className="text-sky-600" />} />
                    <MiniStat label="New users · 7d" value={overview?.users?.new_this_week || 0} icon={<Users size={16} className="text-emerald-600" />} />
                    <MiniStat label="Reviews · 7d" value={overview?.reviews?.new_this_week || 0} icon={<Star size={16} className="text-amber-600" />} />
                    <MiniStat label="SOS · 7d" value={overview?.sos?.week || 0} icon={<Shield size={16} className="text-red-600" />} />
                    <MiniStat label="User-added locations" value={overview?.restrooms?.user_added || 0} icon={<Building2 size={16} className="text-purple-600" />} />
                    <MiniStat label="Sponsorships" value={overview?.sponsorships?.total || 0} icon={<Star size={16} className="text-pink-600" />} />
                    <MiniStat label="Events · 7d" value={overview?.events?.week || 0} icon={<Activity size={16} className="text-slate-500" />} />
                    <MiniStat label="Flagged reviews" value={overview?.reviews?.flagged || 0} icon={<AlertTriangle size={16} className="text-red-600" />} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* GEOGRAPHY */}
            <TabsContent value="geography">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapIcon size={18} className="text-purple-600" />
                    Users by Indian State
                  </CardTitle>
                  {geo.top_user_state && (
                    <p className="text-sm text-slate-500">
                      Peak state: <span className="font-semibold text-slate-800">{geo.top_user_state.state}</span>{" "}
                      with {geo.top_user_state.users?.toLocaleString()} user{geo.top_user_state.users === 1 ? "" : "s"}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <ScrollArea className="max-h-[520px]">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="geo-table">
                        <thead className="text-left text-slate-500 border-b border-slate-100">
                          <tr>
                            <th className="py-2 pr-4">State / UT</th>
                            <th className="py-2 pr-4 text-right">Users</th>
                            <th className="py-2 pr-4 text-right">Locations</th>
                            <th className="py-2 text-right">Events (30d)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(geo.rows || []).map((row) => (
                            <tr key={row.state} className="border-b border-slate-50 hover:bg-slate-50">
                              <td className="py-2 pr-4 font-medium text-slate-800">{row.state}</td>
                              <td className="py-2 pr-4 text-right tabular-nums">{row.users?.toLocaleString?.() || 0}</td>
                              <td className="py-2 pr-4 text-right tabular-nums text-slate-600">{row.restrooms?.toLocaleString?.() || 0}</td>
                              <td className="py-2 text-right tabular-nums text-slate-600">{row.events?.toLocaleString?.() || 0}</td>
                            </tr>
                          ))}
                          {(geo.rows || []).length === 0 && (
                            <tr>
                              <td colSpan={4} className="py-6 text-center text-slate-400">
                                No geographic data yet — users need to interact with the app to populate this.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-lg">Users concentration (top 15)</CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart
                    rows={(geo.rows || [])
                      .filter((r) => r.state && r.state !== "Unknown")
                      .slice(0, 15)
                      .map((r) => ({ label: r.state, count: r.users }))}
                    color="#a855f7"
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}

const StatCard = ({ icon, bg, value, label, testid }) => (
  <Card className="stat-card">
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center`}>{icon}</div>
        <div>
          <p className="text-2xl font-bold text-slate-900" data-testid={testid}>{value?.toLocaleString?.() || 0}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const MiniStat = ({ icon, value, label }) => (
  <Card>
    <CardContent className="pt-4 flex items-center gap-3">
      <div className="shrink-0">{icon}</div>
      <div>
        <p className="text-lg font-semibold">{value?.toLocaleString?.() || 0}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);
